"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Globe as Globe2, Compass, TrendingUp, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function DubaiContext() {
  const shouldReduceMotion = useReducedMotion();
  const insights = [{
    title: "Bilingual Search Architecture",
    description: "The UAE search landscape demands fluent technical architecture across both English and Arabic queries to capture full market intent and avoid cannibalization.",
    icon: Globe2
  }, {
    title: "Hyper-Competitive Regional Verticals",
    description: "From Dubai real estate and luxury hospitality to B2B logistics, standard boilerplate SEO fails against agile brands investing in tailored search strategy.",
    icon: TrendingUp
  }, {
    title: "Direct Strategic Execution",
    description: "You work directly with me — no account managers or junior pass-offs. I personally diagnose, execute, and monitor every technical and content milestone.",
    icon: ShieldCheck
  }];
  const valuePillars = ["Zero reliance on outsourced work or generic agency templates", "Detailed commercial query mapping across UAE and GCC territories", "Pragmatic technical audits focused on actual indexation and crawl efficiency", "Transparent performance reporting aligned with real commercial pipeline"];
  const animateProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 24
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  return <section id="dubaimarketcontext" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      {/* Decorative subtle background accents */}
      <div className="absolute top-0 right-0 -mr-24 -mt-24 h-96 w-96 rounded-full bg-secondary/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-24 -mb-24 h-80 w-80 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div {...animateProps} className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-border bg-secondary">
            Regional Search Dynamics
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground leading-tight">
            Navigating the Dubai &amp; UAE Search Market
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            The UAE digital landscape moves quickly. I help businesses build durable search presence through hands-on technical precision and tailored international SEO strategies.
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {/* Left Column: Direct Expert Commentary */}
          <motion.div {...animateProps} className="lg:col-span-7 space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <Compass className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold font-serif text-foreground">
                  Why Dubai Requires a Specialized SEO Approach
                </h3>
              </div>

              <div className="space-y-4 text-muted-foreground font-sans text-sm sm:text-base leading-relaxed">
                <p>
                  Search in Dubai and the wider UAE operates at the intersection of local intent and international competition. With high digital adoption and expat diversity, consumer search behavior shifts rapidly between high-intent commercial terms and localized queries.
                </p>
                <p>
                  Generic SEO checklists and automated agency retainers consistently miss the regional nuance: correctly structured hreflang tags for Arabic and English targeting, precise local authority signals, and search intent that reflects actual purchase decisions in the Gulf.
                </p>
                <p>
                  I partner directly with founders and marketing leaders to build structured search foundations that withstand core algorithm shifts and compound qualified organic traffic over time.
                </p>
              </div>

              {/* Why SEO Matters subsection */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h4 className="text-lg font-semibold font-serif text-foreground">
                    Why SEO Matters
                  </h4>
                </div>
                <div className="space-y-3 text-muted-foreground font-sans text-sm sm:text-base leading-relaxed">
                  <p>
                    Organic search is the highest-converting channel due to commercial search intent. Most UAE adults use Google Search and Maps daily. Research shows 81% of consumers research online before making a purchase decision.
                  </p>
                  <p>
                    Dubai's multicultural market requires localized SEO strategies — not one-size-fits-all approaches. SEO delivers compounding long-term advantage, progressively reducing your dependence on paid advertising and generating sustainable, cost-effective traffic.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-primary mb-4">
                  My Core Strategic Principles
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {valuePillars.map((pillar, i) => <div key={pillar} data-index={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground">
                      <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                      <span>{pillar}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Strategic Focus Cards */}
          <motion.div {...animateProps} className="lg:col-span-5 flex flex-col gap-4">
            {insights.map((item, i) => {
            const IconComp = item.icon;
            return <Card key={item.title} data-index={i} className="border-border bg-card/80 backdrop-blur-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                  <CardHeader className="flex flex-row items-center gap-4 space-y-0 pb-2">
                    <div className="p-2.5 rounded-lg bg-secondary text-primary">
                      <IconComp className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-base font-semibold font-serif text-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs sm:text-sm text-muted-foreground font-sans leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </motion.div>
        </div>

        {/* Action / Case Study Proof Banner */}
        <motion.div {...animateProps} className="rounded-2xl bg-secondary/70 border border-border p-6 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left max-w-xl">
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              Review Verified Search Case Studies
            </h3>
            <p className="text-sm text-muted-foreground font-sans">
              I document real client challenges, specific strategies, and measurable business outcomes. Review authentic project workflows and verified results.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-lg shadow-sm">
              <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                Explore Case Studies
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-lg">
              <Link to="/ContactPage">
                Discuss Your Market
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}